import { useEffect, useState, Dispatch, SetStateAction } from 'react';
import { usePrevious } from './usePrevious';
import { isEqual } from 'lodash-es';

/**
 * Same as React's standard `useState`, except that if the "initialiser" value changes, we auto update
 * the local state automatically.
 *
 * Good for when you want some state based on an async value (like calling an endpoint).
 *
 * Also supplies an `onSync` callback that is called when the local state is updated.
 *
 * @param syncee The value to keep in sync
 * @param onSync A callback that is called when the local state is updated
 * @returns
 */
export const useSyncState = <T>(syncee: T, onSync?: (newValue: T) => void): [T, Dispatch<SetStateAction<T>>] => {
  const [localState, setLocalState] = useState<T>(syncee);

  const prevInitialValue = usePrevious(syncee, true);

  useEffect(() => {
    // if the new initial value is different from the previous one, update the local one
    if (!isEqual(prevInitialValue, syncee)) {
      setLocalState(syncee);

      if (onSync) {
        onSync(syncee);
      }
    }
  }, [syncee]);

  return [localState, setLocalState];
};
